import React, {useEffect, useState} from "react";
import {Box, FormField, ResponsiveContext, Select, Text, TextInput} from "grommet";
import { useSession } from "next-auth/react";
import Form from "../../Form";
import CustomerTagInput from "../../CustomerTagInput";
import {formatName} from "../../../utils/names";
import Badge from "../../Badge";
import DropButton from "../../DropButton";
import NextAnchor from "../../NextAnchor";
import { DisplayInlineBlockBox } from "../../../utils/styled";

const PaidFeatureBadge = () => (
    <DropButton
        label={<Badge background={{ color: 'lemon', opacity: '0.2' }}>PAID FEATURE</Badge>}
        dropContent={(
            <DisplayInlineBlockBox round="xsmall" width={{ max: 'medium' }} border pad="xsmall">
                <Text size="small">
                    This functionality is available as part of an upgraded plan. <NextAnchor hoverIndicator target="_blank" href="https://streamlinedpayments.com/pricing">Learn more</NextAnchor>
                </Text>
            </DisplayInlineBlockBox>
        )}
        dropProps={{ align: { top: 'bottom' } }}
        plain
    />
);

const CustomerGroupForm = ({
    onSubmit,
    onCancel,
    values,
    companyNameRequired,
    customers,
    ...rest
}) => {
    const { data: session } = useSession();
    const hasPlan = session?.company?.has_scale_plan || session?.company?.has_enterprise_plan;
    const [formValues, setFormValues] = useState(values || {
        company_name: '',
        customer_ids: []
    });

    useEffect(() => {
        values && setFormValues(values);
    }, [values]);

    return (
        <ResponsiveContext.Consumer>
            { (size) => (
                <Form
                    value={formValues}
                    editOnly
                    sectionProps={{
                        margin: {bottom: 'medium'},
                        border: false,
                        width: (size === 'small' ? 'auto' : 'medium'),
                        pad: {bottom: 'medium'},
                        headerProps: {
                            border: false,
                        },
                        elevation: 'none',
                    }}
                    onChange={(nextValue, {touched}) => {
                        setFormValues(nextValue);
                    }}
                    onReset={(event) => {
                        onCancel && onCancel(event);
                    }}
                    onSubmit={(event) => {
                        if (!hasPlan) {
                            const { billing_customer_uuid, ...rest } = event.value;
                            onSubmit({ ...event, value: rest });
                        } else {
                            onSubmit(event);
                        }
                    }}
                    {...rest}
                >
                    <FormField
                        label="Group name"
                        name="company_name"
                        required
                    >
                        <TextInput
                            name="company_name"
                            placeholder="Group name"
                            autoComplete="chrome-off"
                            autoFocus
                        />
                    </FormField>
                    <FormField
                        label="Customers"
                        name="customer_ids"
                        validate={(customers) => {
                            if (!customers || customers.length < 1) return 'You must select at least one customer to form a group';
                        }}
                        component={CustomerTagInput}
                        plain
                    />
                    {customers && customers.length > 0 && (
                        <FormField
                            label={
                                <Box direction="row" gap="xsmall" align="center">
                                    <Text>Billing Account</Text>
                                    {!hasPlan && <PaidFeatureBadge />}
                                </Box>
                            }
                            name="billing_customer_uuid"
                            help="Billing details from this account apply to all customers in the group"
                        >
                            <Select
                                name="billing_customer_uuid"
                                placeholder="None"
                                clear={{ label: "Clear billing account" }}
                                labelKey={(c) => c.company_name || formatName(c, true)}
                                valueKey={{ key: "uuid", reduce: true }}
                                options={customers}
                                disabled={!hasPlan}
                            />
                        </FormField>
                    )}
                </Form>
            )}
        </ResponsiveContext.Consumer>
    );
};

export default CustomerGroupForm;
